import { unzlibSync } from "fflate";
import { JellyfinItem } from "../api/jellyfin";
import { Credentials } from "../storage/credentials";

const accentCache = new Map<string, string>();
const controlTintCache = new Map<string, CardControlTints>();

export type CardControlTints = {
  play: string;
  playActive: string;
  info: string;
  infoActive: string;
  check: string;
  checkActive: string;
};

export const CARD_CONTROL_TINT_VERSION = 26;

type DecodedPng = {
  width: number;
  height: number;
  channels: number;
  colorType: number;
  pixels: Uint8Array;
};

export function getColorChannels(
  color: string,
  fallback: readonly [number, number, number] = [229, 9, 20],
): [number, number, number] {
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})(?:[0-9a-f]{2})?$/i)?.[1];
  if (hex) {
    const expanded =
      hex.length === 3
        ? hex
            .split("")
            .map((channel) => `${channel}${channel}`)
            .join("")
        : hex;
    return [0, 2, 4].map((offset) =>
      Number.parseInt(expanded.slice(offset, offset + 2), 16),
    ) as [number, number, number];
  }
  const numeric = color.match(/\d+(?:\.\d+)?/g)?.slice(0, 3).map(Number);
  if (numeric?.length === 3 && numeric.every(Number.isFinite)) {
    return numeric.map((channel) => Math.max(0, Math.min(255, channel))) as [
      number,
      number,
      number,
    ];
  }
  return [...fallback];
}

function paeth(left: number, above: number, upperLeft: number) {
  const estimate = left + above - upperLeft;
  const leftDistance = Math.abs(estimate - left);
  const aboveDistance = Math.abs(estimate - above);
  const cornerDistance = Math.abs(estimate - upperLeft);
  return leftDistance <= aboveDistance && leftDistance <= cornerDistance
    ? left
    : aboveDistance <= cornerDistance
      ? above
      : upperLeft;
}

function relativeLuminance(red: number, green: number, blue: number) {
  const values = [red, green, blue].map((channel) => {
    const value = channel / 255;
    return value <= 0.04045
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * values[0] + 0.7152 * values[1] + 0.0722 * values[2];
}

function rgbToHsl(red: number, green: number, blue: number) {
  const [r, g, b] = [red, green, blue].map((value) => value / 255);
  const maximum = Math.max(r, g, b);
  const minimum = Math.min(r, g, b);
  const lightness = (maximum + minimum) / 2;
  if (maximum === minimum) return [0, 0, lightness] as const;
  const delta = maximum - minimum;
  const saturation =
    lightness > 0.5
      ? delta / (2 - maximum - minimum)
      : delta / (maximum + minimum);
  const hue =
    maximum === r
      ? ((g - b) / delta + (g < b ? 6 : 0)) / 6
      : maximum === g
        ? ((b - r) / delta + 2) / 6
        : ((r - g) / delta + 4) / 6;
  return [hue, saturation, lightness] as const;
}

function hslToRgb(hue: number, saturation: number, lightness: number) {
  if (!saturation) {
    const gray = Math.round(lightness * 255);
    return [gray, gray, gray] as const;
  }
  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;
  const channel = (input: number) => {
    let value = input;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    const result =
      value < 1 / 6
        ? p + (q - p) * 6 * value
        : value < 1 / 2
          ? q
          : value < 2 / 3
            ? p + (q - p) * (2 / 3 - value) * 6
            : p;
    return Math.round(result * 255);
  };
  return [channel(hue + 1 / 3), channel(hue), channel(hue - 1 / 3)] as const;
}

function tuneLikeJellyflix(red: number, green: number, blue: number) {
  const [hue, saturation, startingLightness] = rgbToHsl(red, green, blue);
  const tunedSaturation = Math.min(1, saturation + 0.18);
  let lightness = startingLightness;
  let tuned = hslToRgb(hue, tunedSaturation, lightness);
  while (
    relativeLuminance(tuned[0], tuned[1], tuned[2]) < 0.28 &&
    lightness < 1
  ) {
    lightness = Math.min(1, lightness + 0.06);
    tuned = hslToRgb(hue, tunedSaturation, lightness);
  }
  return `rgb(${tuned[0]},${tuned[1]},${tuned[2]})`;
}

function decodePng(bytes: Uint8Array): DecodedPng {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 8;
  let width = 0;
  let height = 0;
  let colorType = 6;
  const compressed: Uint8Array[] = [];
  while (offset + 12 <= bytes.length) {
    const length = view.getUint32(offset);
    const type = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7],
    );
    const dataStart = offset + 8;
    if (type === "IHDR") {
      width = view.getUint32(dataStart);
      height = view.getUint32(dataStart + 4);
      if (bytes[dataStart + 8] !== 8)
        throw new Error("Unsupported PNG bit depth");
      colorType = bytes[dataStart + 9];
    } else if (type === "IDAT")
      compressed.push(bytes.slice(dataStart, dataStart + length));
    else if (type === "IEND") break;
    offset = dataStart + length + 4;
  }
  const channels =
    colorType === 6
      ? 4
      : colorType === 2
        ? 3
        : colorType === 4
          ? 2
          : colorType === 0
            ? 1
            : 0;
  if (!width || !height || !channels || !compressed.length)
    throw new Error("Unsupported PNG layout");
  const compressedLength = compressed.reduce(
    (sum, part) => sum + part.length,
    0,
  );
  const combined = new Uint8Array(compressedLength);
  let cursor = 0;
  for (const part of compressed) {
    combined.set(part, cursor);
    cursor += part.length;
  }
  const inflated = unzlibSync(combined);
  const stride = width * channels;
  const pixels = new Uint8Array(stride * height);
  let source = 0;
  for (let row = 0; row < height; row += 1) {
    const filter = inflated[source++];
    const rowStart = row * stride;
    for (let column = 0; column < stride; column += 1) {
      const raw = inflated[source++];
      const left =
        column >= channels ? pixels[rowStart + column - channels] : 0;
      const above = row > 0 ? pixels[rowStart - stride + column] : 0;
      const upperLeft =
        row > 0 && column >= channels
          ? pixels[rowStart - stride + column - channels]
          : 0;
      const predictor =
        filter === 1
          ? left
          : filter === 2
            ? above
            : filter === 3
              ? Math.floor((left + above) / 2)
              : filter === 4
                ? paeth(left, above, upperLeft)
                : 0;
      pixels[rowStart + column] = (raw + predictor) & 255;
    }
  }
  return { width, height, channels, colorType, pixels };
}

function readPngDominant(bytes: Uint8Array) {
  const { width, height, channels, colorType, pixels } = decodePng(bytes);
  const ratio = width / height;
  const destinationWidth = width > height ? 100 : Math.round(100 * ratio);
  const destinationHeight = width > height ? Math.round(100 / ratio) : 100;
  let redTotal = 0;
  let greenTotal = 0;
  let blueTotal = 0;
  let alphaTotal = 0;
  for (
    let destinationY = 0;
    destinationY < destinationHeight;
    destinationY += 1
  ) {
    const sourceY = Math.min(
      height - 1,
      Math.floor(((destinationY + 0.5) * height) / destinationHeight),
    );
    for (
      let destinationX = 0;
      destinationX < destinationWidth;
      destinationX += 1
    ) {
      const sourceX = Math.min(
        width - 1,
        Math.floor(((destinationX + 0.5) * width) / destinationWidth),
      );
      const index = (sourceY * width + sourceX) * channels;
      const alpha =
        colorType === 6
          ? pixels[index + 3]
          : colorType === 4
            ? pixels[index + 1]
            : 255;
      const gray = colorType === 0 || colorType === 4;
      const red = pixels[index];
      const green = gray ? pixels[index] : pixels[index + 1];
      const blue = gray ? pixels[index] : pixels[index + 2];
      redTotal += red * alpha;
      greenTotal += green * alpha;
      blueTotal += blue * alpha;
      alphaTotal += alpha;
    }
  }
  if (!alphaTotal) throw new Error("PNG has no visible average color");
  let red = Math.round(redTotal / alphaTotal);
  let green = Math.round(greenTotal / alphaTotal);
  let blue = Math.round(blueTotal / alphaTotal);
  let [hue, saturation, lightness] = rgbToHsl(red, green, blue);
  while ((red * 299 + green * 587 + blue * 114) / 1000 > 128) {
    lightness = Math.max(0, lightness - 0.1);
    [red, green, blue] = hslToRgb(hue, saturation, lightness);
  }
  while (relativeLuminance(red, green, blue) < 0.22) {
    lightness = Math.min(1, lightness + 0.06);
    [red, green, blue] = hslToRgb(hue, saturation, lightness);
  }
  return `rgb(${red},${green},${blue})`;
}

function readPngMode(bytes: Uint8Array, darkenLight = true) {
  const { width, height, channels, colorType, pixels } = decodePng(bytes);
  const bins = new Map<
    string,
    { count: number; red: number; green: number; blue: number }
  >();
  const ratio = width / height;
  const destinationWidth = width > height ? 100 : Math.round(100 * ratio);
  const destinationHeight = width > height ? Math.round(100 / ratio) : 100;
  for (
    let destinationY = 0;
    destinationY < destinationHeight;
    destinationY += 1
  ) {
    const y = Math.min(
      height - 1,
      Math.floor((destinationY * height) / destinationHeight),
    );
    for (
      let destinationX = 0;
      destinationX < destinationWidth;
      destinationX += 1
    ) {
      const x = Math.min(
        width - 1,
        Math.floor((destinationX * width) / destinationWidth),
      );
      const index = (y * width + x) * channels;
      const alpha =
        colorType === 6
          ? pixels[index + 3]
          : colorType === 4
            ? pixels[index + 1]
            : 255;
      if (alpha < 128) continue;
      const gray = colorType === 0 || colorType === 4;
      const red = pixels[index];
      const green = gray ? red : pixels[index + 1];
      const blue = gray ? red : pixels[index + 2];
      if (![red, green, blue].every(Number.isFinite)) continue;
      // fast-average-color's dominant algorithm groups samples by rounded
      // 24-channel buckets. Keep this identical so music accents agree
      // with Jellyflix without relying on a DOM canvas at native runtime.
      const key = `${Math.round(red / 24)},${Math.round(green / 24)},${Math.round(blue / 24)}`;
      const bin = bins.get(key) ?? { count: 0, red: 0, green: 0, blue: 0 };
      bin.count += 1;
      bin.red += red;
      bin.green += green;
      bin.blue += blue;
      bins.set(key, bin);
    }
  }
  let dominant:
    | { count: number; red: number; green: number; blue: number }
    | undefined;
  bins.forEach((candidate) => {
    if (!dominant || candidate.count > dominant.count) dominant = candidate;
  });
  if (!dominant) return "rgb(30,30,30)";
  let red = Math.round(dominant.red / dominant.count);
  let green = Math.round(dominant.green / dominant.count);
  let blue = Math.round(dominant.blue / dominant.count);
  let [hue, saturation, lightness] = rgbToHsl(red, green, blue);
  while (darkenLight && (red * 299 + green * 587 + blue * 114) / 1000 > 128) {
    lightness = Math.max(0, lightness - 0.1);
    [red, green, blue] = hslToRgb(hue, saturation, lightness);
  }
  return `rgb(${red},${green},${blue})`;
}

export async function getMusicHomeHeaderAccent(
  credentials: Credentials,
  item: JellyfinItem,
) {
  const key = `${item.Id}:music-home-header`;
  const cached = accentCache.get(key);
  if (cached) return cached;
  if (!item.Id) return "rgb(30,30,30)";
  const imageTag = item.ImageTags?.Primary;
  const response = await fetch(
    `${credentials.serverUrl}/Items/${encodeURIComponent(item.Id)}/Images/Primary?width=200&quality=90&format=png${imageTag ? `&tag=${encodeURIComponent(imageTag)}` : ""}`,
    { headers: { "X-Emby-Token": credentials.accessToken } },
  );
  if (!response.ok)
    throw new Error(`Music home header sample failed: ${response.status}`);
  const accent = readPngMode(new Uint8Array(await response.arrayBuffer()), false);
  accentCache.set(key, accent);
  return accent;
}

function samplePngRegion(
  decoded: DecodedPng,
  centerX: number,
  centerY: number,
  radiusX = 0.1,
  radiusY = 0.16,
) {
  const { width, height, channels, colorType, pixels } = decoded;
  const minimumX = Math.max(0, Math.floor((centerX - radiusX) * width));
  const maximumX = Math.min(width - 1, Math.ceil((centerX + radiusX) * width));
  const minimumY = Math.max(0, Math.floor((centerY - radiusY) * height));
  const maximumY = Math.min(
    height - 1,
    Math.ceil((centerY + radiusY) * height),
  );
  let redTotal = 0;
  let greenTotal = 0;
  let blueTotal = 0;
  let weightTotal = 0;
  for (let y = minimumY; y <= maximumY; y += 1) {
    for (let x = minimumX; x <= maximumX; x += 1) {
      const normalizedX = (x / Math.max(1, width - 1) - centerX) / radiusX;
      const normalizedY = (y / Math.max(1, height - 1) - centerY) / radiusY;
      const distance = normalizedX * normalizedX + normalizedY * normalizedY;
      if (distance > 1) continue;
      const index = (y * width + x) * channels;
      const alpha =
        colorType === 6
          ? pixels[index + 3] / 255
          : colorType === 4
            ? pixels[index + 1] / 255
            : 1;
      const gray = colorType === 0 || colorType === 4;
      const weight = Math.max(0.08, 1 - distance) * alpha;
      redTotal += pixels[index] * weight;
      greenTotal += (gray ? pixels[index] : pixels[index + 1]) * weight;
      blueTotal += (gray ? pixels[index] : pixels[index + 2]) * weight;
      weightTotal += weight;
    }
  }
  if (!weightTotal) return [72, 78, 92] as const;
  return [
    Math.round(redTotal / weightTotal),
    Math.round(greenTotal / weightTotal),
    Math.round(blueTotal / weightTotal),
  ] as const;
}

function blurredSurface(
  rgb: readonly [number, number, number],
  artworkWeight: number,
  whiteWeight = 0,
) {
  const channels = rgb.map((channel) =>
    Math.round(channel * artworkWeight + 255 * whiteWeight),
  );
  return `rgb(${channels[0]},${channels[1]},${channels[2]})`;
}

export async function getCardControlTints(
  credentials: Credentials,
  item: JellyfinItem,
  kind: "Primary" | "Backdrop" | "Thumb" = "Primary",
) {
  // The rendered top-right group is anchored from its outer edge: watched is
  // the inner/left action and info is the outer/right action. Keep this
  // versioned so a hot Web session cannot retain the previously swapped
  // button-region assignments.
  const key = `${item.Id}:${kind}:controls-v${CARD_CONTROL_TINT_VERSION}`;
  const cached = controlTintCache.get(key);
  if (cached) return cached;
  if (!item.Id) throw new Error("Card tint requires an item");
  const response = await fetch(
    `${credentials.serverUrl}/Items/${encodeURIComponent(item.Id)}/Images/${kind}?fillWidth=160&fillHeight=90&quality=82&format=png`,
    {
      headers: { "X-Emby-Token": credentials.accessToken },
    },
  );
  if (!response.ok)
    throw new Error(`Card tint sample failed: ${response.status}`);
  const decoded = decodePng(new Uint8Array(await response.arrayBuffer()));
  const play = samplePngRegion(decoded, 0.5, 0.5, 0.13, 0.22);
  // The rendered group is [watched, info], from left to right.
  const check = samplePngRegion(decoded, 0.79, 0.16, 0.07, 0.13);
  const info = samplePngRegion(decoded, 0.92, 0.16, 0.07, 0.13);
  const tints: CardControlTints = {
    play: blurredSurface(play, 0.52),
    playActive: blurredSurface(play, 0.82, 0.18),
    info: blurredSurface(info, 0.62),
    infoActive: blurredSurface(info, 0.84, 0.16),
    check: blurredSurface(check, 0.62),
    checkActive: blurredSurface(check, 0.84, 0.16),
  };
  controlTintCache.set(key, tints);
  return tints;
}

export async function getImageAccent(
  credentials: Credentials,
  item: JellyfinItem,
  kind: "Primary" | "Backdrop" = "Primary",
) {
  const key = `${item.Id}:${kind}`;
  const cached = accentCache.get(key);
  if (cached) return cached;
  if (!item.Id) return "rgb(72,78,92)";
  const imagePath = kind === "Backdrop" ? "Backdrop/0" : kind;
  const response = await fetch(
    `${credentials.serverUrl}/Items/${encodeURIComponent(item.Id)}/Images/${imagePath}?width=1920&quality=90&format=png`,
    {
      headers: { "X-Emby-Token": credentials.accessToken },
    },
  );
  if (!response.ok) throw new Error(`Accent sample failed: ${response.status}`);
  let accent = readPngDominant(new Uint8Array(await response.arrayBuffer()));
  if (kind === "Backdrop") {
    const [red, green, blue] = getColorChannels(accent, [72, 78, 92]);
    accent = `rgb(${Math.max(0, red - 1)},${green},${Math.max(0, blue - 3)})`;
  }
  accentCache.set(key, accent);
  return accent;
}

export async function getMusicHeaderAccent(
  credentials: Credentials,
  item: JellyfinItem,
) {
  const key = `${item.Id}:music-header`;
  const cached = accentCache.get(key);
  if (cached) return cached;
  if (!item.Id) return "rgb(30,30,30)";
  const imageTag = item.ImageTags?.Primary;
  const response = await fetch(
    `${credentials.serverUrl}/Items/${encodeURIComponent(item.Id)}/Images/Primary?width=400&quality=90&format=png${imageTag ? `&tag=${encodeURIComponent(imageTag)}` : ""}`,
    { headers: { "X-Emby-Token": credentials.accessToken } },
  );
  if (!response.ok)
    throw new Error(`Music header sample failed: ${response.status}`);
  const accent = readPngMode(new Uint8Array(await response.arrayBuffer()));
  accentCache.set(key, accent);
  return accent;
}

export async function getMusicArtistHeaderAccent(
  credentials: Credentials,
  item: JellyfinItem,
) {
  const key = `${item.Id}:music-artist-header`;
  const cached = accentCache.get(key);
  if (cached) return cached;
  const sampled = await getMusicHeaderAccent(credentials, item);
  const [red, green, blue] = getColorChannels(sampled, [0, 0, 0]);
  // Match Jellyflix's neutral artist surface when transparent or very dark
  // portrait pixels collapse into the native decoder's empty canvas bucket.
  const accent =
    relativeLuminance(red, green, blue) < 0.025 ? "rgb(126,126,126)" : sampled;
  accentCache.set(key, accent);
  return accent;
}

export function getCachedImageAccent(
  item: JellyfinItem,
  kind: "Primary" | "Backdrop" = "Primary",
) {
  return accentCache.get(`${item.Id}:${kind}`);
}

export function getAccentPresentation(accent: string) {
  const [red, green, blue] = getColorChannels(accent);
  const [hue, saturation, lightness] = rgbToHsl(red, green, blue);
  const title = hslToRgb(
    hue,
    Math.min(1, saturation + 0.18),
    Math.min(1, lightness + 0.1),
  );
  return {
    titleColor: `rgb(${title[0]},${title[1]},${title[2]})`,
    textColor:
      relativeLuminance(red, green, blue) >= 0.179 ? "#050505" : "#ffffff",
  };
}
